<template>
	<div class="upload-drag-wrapper">
		<!-- 拖拽上传区域 -->
		<div
			class="drag-upload-area"
			:class="{ 'is-disabled': isDisabled || uploadFileList.length >= limit, 'is-dragover': isDragOver }"
			@dragover.prevent="handleDragOver"
			@dragleave.prevent="handleDragLeave"
			@drop.prevent="handleDrop"
		>
			<el-upload
				ref="uploadRef"
				class="drag-uploader"
				:action="action"
				:data="data"
				:headers="headers"
				:accept="accept"
				:limit="limit"
				:show-file-list="false"
				:before-upload="beforeImagesUpload"
				:on-exceed="handleExceed"
				:on-success="handleImagesSuccess"
				:on-error="handleImagesError"
				:disabled="isDisabled || uploadFileList.length >= limit"
				v-bind="$attrs"
				drag
			>
				<div v-if="uploadFileList.length < limit" class="drag-content">
					<div class="drag-icon">
						<i class="el-icon-picture-outline"></i>
					</div>
					<div class="drag-text">
						<p class="main-text">点击选择或拖拽上传展示图片</p>
						<p class="sub-text">支持 {{ acceptText }} 格式，单张不超过 {{ size }}MB，最多上传 {{ limit }} 张</p>
					</div>
					<el-button type="primary" size="small" class="select-btn" :disabled="isDisabled || uploadFileList.length >= limit">
						选择图片
					</el-button>
				</div>
				<div v-else class="drag-content">
					<div class="drag-icon">
						<i class="el-icon-check"></i>
					</div>
					<div class="drag-text">
						<p class="main-text">已达到上传上限</p>
						<p class="sub-text">已上传 {{ uploadFileList.length }} 张图片，如需更换请先删除已有图片</p>
					</div>
				</div>
			</el-upload>
		</div>

		<!-- 上传中遮罩 -->
		<div v-if="isUploading" class="upload-loading-mask">
			<i class="el-icon-loading"></i>
			<span>上传中...</span>
		</div>

		<!-- 图片列表 -->
		<div v-if="uploadFileList.length > 0" class="upload-images-list">
			<div v-for="(item, index) in uploadFileList" :key="index" class="upload-images">
				<el-image
					ref="imageRef"
					:src="item.url"
					:preview-src-list="uploadFileList.map(item => item.url)"
					:initial-index="index"
					class="upload-image-item"
				></el-image>
				<div class="upload-mask">
					<div class="actions">
						<i class="action-icon el-icon-zoom-in" @click="previewImage(index)"></i>
						<i v-if="!isDisabled" class="action-icon el-icon-delete" @click="deleteImage(index)"></i>
					</div>
				</div>
			</div>
		</div>

		<!-- 提示信息 -->
		<div v-if="tip" class="images-uploader-tip">
			最多上传{{ limit }}张图片，大小不能超过{{ size }}MB，且仅支持{{ acceptText }}格式
		</div>
	</div>
</template>

<!-- 拖拽上传图片组件 -->
<script>
	import { MessageBox } from 'element-ui'

	export default {
		name: 'UploadDragImages',
		components: {},
		inheritAttrs: false,
		model: {
			prop: 'modelValue',
			event: 'update:modelValue'
		},
		props: {
			headers: {
				type: Object,
				default: () => ({})
			},
			action: {
				type: String,
				required: true
			},
			data: {
				type: Object,
				default: () => ({})
			},
			limit: {
				type: Number,
				default: 1
			},
			size: {
				type: Number,
				default: 5
			},
			accept: {
				type: String,
				default: '.jpg,.jpeg,.png,.gif,.webp'
			},
			tip: {
				type: Boolean,
				default: false
			},
			modelValue: {
				type: Array,
				default: () => []
			},
			queryApi: {
				type: Function,
				default: null
			},
			deleteApi: {
				type: Function,
				default: null
			},
			queryParams: {
				type: Object,
				default: () => ({})
			},
			deleteParams: {
				type: Object,
				default: () => ({})
			},
			previewUrl: {
				type: String,
				default: ''
			},
			disabled: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				uploadFileList: [],
				isUploading: false,
				isDragOver: false
			}
		},
		computed: {
			isDisabled() {
				if (this.disabled) return true
				let parent = this.$parent
				while (parent) {
					const parentProps = parent.$options.propsData || {}
					if (parentProps.disabled !== undefined) {
						return parentProps.disabled
					}
					parent = parent.$parent
				}
				return false
			},
			acceptText() {
				return this.accept
					.split(',')
					.map(ext => ext.replace('.', '').toUpperCase())
					.join('、')
			}
		},
		watch: {
			modelValue: {
				handler(newVal) {
					if (newVal && Array.isArray(newVal)) {
						this.uploadFileList = [...newVal]
					}
				},
				immediate: true,
				deep: true
			}
		},
		mounted() {
			// this.loadImages()
		},
		methods: {
			/**
			 * 拖拽进入
			 */
			handleDragOver() {
				if (this.isDisabled || this.uploadFileList.length >= this.limit) return
				this.isDragOver = true
			},
			/**
			 * 拖拽离开
			 */
			handleDragLeave() {
				this.isDragOver = false
			},
			/**
			 * 拖拽放下
			 */
			handleDrop() {
				this.isDragOver = false
			},
			/**
			 * 上传图片前的校验
			 * @param {File} file 上传的文件
			 * @returns {boolean} 是否校验通过
			 */
			beforeImagesUpload(file) {
				const fileExtension =
					'.' +
					file.name
						.split('.')
						.pop()
						.toLowerCase()
				const allowedExtensions = this.accept.split(',').map(ext => ext.trim().toLowerCase())
				const isImage = allowedExtensions.includes(fileExtension)
				const isSize = file.size / 1024 / 1024 < this.size

				if (!isImage) {
					this.$message.error(`上传图片只能是${this.accept}格式!`)
				}
				if (!isSize) {
					this.$message.error(`上传图片大小不能超过${this.size}MB!`)
				}
				if (isImage && isSize) {
					this.isUploading = true
				}
				return isImage && isSize
			},
			/**
			 * 超出上传数量的处理
			 */
			handleExceed() {
				this.$message.error(`最多只能上传${this.limit}张图片!`)
			},
			/**
			 * 上传图片成功的处理
			 * @param {object} res 上传成功的响应数据
			 * @param {File} file 上传的文件
			 */
			handleImagesSuccess(res, file) {
				this.isUploading = false
				if (res.code === 200) {
					const imageFileId = res.data
					if (imageFileId && this.previewUrl) {
						const imageUrl = this.previewUrl + imageFileId
						const imageFileObj = {
							fileId: imageFileId,
							url: imageUrl
						}
						this.uploadFileList.push(imageFileObj)
						this.$emit('update:modelValue', this.uploadFileList)
						this.$emit('uploadSuccess', imageFileObj, this.uploadFileList)
						this.$message.success('上传成功')
					} else {
						console.error('上传成功但未返回图片id或未配置预览地址')
					}
				} else {
					this.$message.error(res.msg || '上传失败')
				}
			},
			/**
			 * 上传图片失败的处理
			 */
			handleImagesError() {
				this.isUploading = false
				this.$message.error('上传失败')
				// 清除 el-upload 内部文件列表，避免上传失败文件占用 limit 计数
				this.$refs.uploadRef && this.$refs.uploadRef.clearFiles()
			},
			/**
			 * 预览图片
			 * @param {number} index 图片索引
			 */
			previewImage(index) {
				const imageInstance = this.$refs.imageRef && this.$refs.imageRef[index]
				if (imageInstance) {
					imageInstance.clickHandler()
				}
			},
			/**
			 * 删除图片
			 * @param {number} index 图片索引
			 */
			deleteImage(index) {
				if (this.isDisabled) return
				const imageItem = this.uploadFileList[index]
				MessageBox.confirm('确定要删除这张图片吗?', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				})
					.then(() => {
						if (this.deleteApi) {
							return this.deleteApi({ ...this.deleteParams, fileRecordId: imageItem.fileId })
								.then(() => {
									this.uploadFileList.splice(index, 1)
								})
								.catch(error => {
									console.error('删除图片失败:', error)
									throw error
								})
						} else {
							this.uploadFileList.splice(index, 1)
							return Promise.resolve()
						}
					})
					.then(() => {
						this.$emit('update:modelValue', this.uploadFileList)
						this.$emit('deleteSuccess', imageItem, this.uploadFileList)
						// 清除 el-upload 内部文件列表，避免上传失败文件占用 limit 计数
						this.$refs.uploadRef && this.$refs.uploadRef.clearFiles()
						this.$message.success('删除成功')
					})
					.catch(error => {
						if (error !== 'cancel') {
							this.$message.error('删除失败')
						}
					})
			},
			/**
			 * 加载图片
			 */
			loadImages() {
				if (this.queryApi) {
					this.queryApi({ ...this.queryParams })
						.then(res => {
							if (res.code === 200 && res.data) {
								const images = res.data.map(item => ({
									fileId: item.id,
									url: this.previewUrl + item.id
								}))
								this.uploadFileList = images
								this.$emit('update:modelValue', this.uploadFileList)
							}
						})
						.catch(error => {
							console.error('加载图片失败:', error)
						})
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.upload-drag-wrapper {
		position: relative;
	}

	.drag-upload-area {
		position: relative;
		border: 2px dashed #d9d9d9;
		border-radius: 8px;
		background: #fafafa;
		cursor: pointer;
		transition: all 0.3s;
		padding: 40px 20px;
		text-align: center;
		overflow: hidden;

		&:hover {
			border-color: #409eff;
			background: #f0f9ff;
		}

		&.is-dragover {
			border-color: #409eff;
			background: #f0f9ff;
			box-shadow: 0 0 10px rgba(64, 158, 255, 0.2);
		}

		&.is-dragover:hover {
			border-color: #409eff;
			background: #f0f9ff;
		}

		&.is-disabled {
			cursor: not-allowed;
			opacity: 0.6;
			border-color: #d9d9d9;
			background: #fafafa;

			&:hover {
				border-color: #d9d9d9;
				background: #fafafa;
			}
		}
	}

	.drag-uploader {
		::v-deep .el-upload {
			width: 100%;
		}

		::v-deep .el-upload-dragger {
			width: 100%;
			background: transparent;
			border: none;
			border-radius: 0;
			padding: 0;
		}
	}

	.drag-content {
		pointer-events: none;
	}

	.drag-icon {
		font-size: 48px;
		color: #c0c4cc;

		i {
			font-size: 48px;
		}
	}

	.drag-text {
		.main-text {
			font-size: 16px;
			color: #606266;
		}

		.sub-text {
			font-size: 13px;
			color: #909399;
		}
	}

	.select-btn {
		pointer-events: none;
	}

	.upload-loading-mask {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(255, 255, 255, 0.9);
		z-index: 10;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		border-radius: 8px;

		i {
			font-size: 32px;
			color: #409eff;
			margin-bottom: 8px;
		}

		span {
			font-size: 14px;
			color: #606266;
		}
	}

	.upload-images-list {
		display: flex;
		flex-wrap: wrap;
		margin-top: 16px;
	}

	.upload-images {
		position: relative;
		display: inline-block;
		width: 120px;
		height: 120px;
		margin-right: 10px;
		margin-bottom: 10px;
		background: #ffffff;
		border: 1px solid #e1e1e1;
		border-radius: 4px;
		overflow: hidden;

		.upload-image-item {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.upload-mask {
			position: absolute;
			top: 0;
			left: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;
			background: rgba(0, 0, 0, 0.4);
			opacity: 0;
			transition: all 0.3s;

			.actions {
				display: flex;
				align-items: center;
				justify-content: center;

				.action-icon {
					margin: 0 8px;
					font-size: 22px;
					color: #ffffff;
					cursor: pointer;

					&:hover {
						color: #409eff;
					}
				}
			}
		}

		&:hover .upload-mask {
			opacity: 1;
		}
	}

	.images-uploader-tip {
		margin-top: 8px;
		font-size: 12px;
		color: #606266;
	}
</style>
